import { clsx } from "clsx";
import React from "react";
import { twMerge } from "tailwind-merge";

export type RadioGroupOption<T> = {
  value: T;
  label: React.ReactNode;
  testId?: string;
};

export type RadioGroupProps<T> = {
  name: string;
  options: RadioGroupOption<T>[];
  value?: T;
  onChange: (value: T) => void;
  orientation?: "vertical" | "horizontal";
  isError?: boolean;
  className?: string;
};

export function RadioGroup<T>({
  name,
  options,
  value,
  onChange,
  orientation = "vertical",
  isError,
  className,
}: RadioGroupProps<T>) {
  const isHorizontal = orientation === "horizontal";
  return (
    <div
      className={twMerge(
        "flex",
        isHorizontal ? "flex-row gap-lg" : "flex-col gap-3",
        className,
      )}
    >
      {options.map((option, i) => {
        const isSelected = option.value === value;
        return (
          <label
            key={i}
            data-testid={option.testId ?? "radio-option"}
            className={clsx(
              "flex cursor-pointer gap-2 rounded-xl border bg-primary ring-brand",
              isHorizontal
                ? "h-11 flex-1 items-center px-xl"
                : "items-start p-4",
              isSelected && "border-brand ring-1",
              !isSelected && (isError ? "border-error" : "border-secondary"),
            )}
          >
            <input
              type="radio"
              name={name}
              checked={isSelected}
              onChange={() => {
                onChange(option.value);
              }}
              className={clsx(
                "size-4 shrink-0 appearance-none rounded-full bg-primary ring-brand",
                !isHorizontal && "mt-0.5",
                isSelected && "border-[5px] border-brand-solid",
                !isSelected &&
                  (isError ? "border border-error" : "border border-primary"),
              )}
            />
            <span
              className={clsx(
                "min-w-0 text-sm font-medium leading-5 text-secondary",
                !isHorizontal && "flex-1",
              )}
            >
              {option.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}
